"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const oneEntry_1 = require("../base/oneEntry");
/**
 * Controllers for working with product pages
 */
class ProductApi extends oneEntry_1.default {
    constructor(url, token) {
        super(url, token);
        this._defaultQuery = {
            offset: 0,
            limit: 30,
            statusMarker: null,
            conditionValue: null,
            conditionMarker: null,
            attributeMarker: null,
            sortOrder: null,
            sortKey: null
        };
        this._url += '/api/content/products';
    }
    /**
     * Search for all product page objects with pagination and filtering.
     *
     * @param {string | Array<string>} [langCode] - Language code parameter. Default "en_US"
     * @param {IProductsQuery} [userQuery] - Optional set query parameters.
     * @param {number} [userQuery.limit] - Optional parameter for pagination, default is 0
     *
     * @param {number} [userQuery.offset] - Optional parameter for pagination, default is 30
     *
     * @param {string} [userQuery.statusMarker] - Optional identifier of the product page status
     *
     * @param {string} [userQuery.conditionValue] - Optional value that is being searched
     *
     * @param {string} [userQuery.conditionMarker] - Optional identifier of the filter condition by which values are filtered
     *
     * @param {string} [userQuery.attributeMarker] - Optional text identifier of the indexed attribute by which values are filtered
     *
     * @param {string} [userQuery.sortOrder] - Optional sorting order DESC | ASC
     *
     * @param {string} [userQuery.sortKey] - Optional field to sort by
     *
     * @returns Array with ProductEntity objects
     */
    async getProducts(langCode = 'en_US', userQuery) {
        const query = { ...this._defaultQuery, ...userQuery };
        const result = await this._fetchRequests(`?langCode=${this._LANGCODE_KEY}&` + this._queryParamsToString(query), langCode);
        return result;
    }
    /**
     * Search for all product page objects with pagination that do not have a category.
     *
     * @param {string | Array<string>} [langCode] - Language code parameter. Default "en_US"
     * @param {IProductsQuery} [userQuery] - Optional set query parameters.
     * @param {number} [userQuery.limit] - Optional parameter for pagination, default is 0
     *
     * @param {number} [userQuery.offset] - Optional parameter for pagination, default is 30
     *
     * @param {string} [userQuery.statusMarker] - Optional identifier of the product page status
     *
     * @param {string} [userQuery.conditionValue] - Optional value that is being searched
     *
     * @param {string} [userQuery.conditionMarker] - Optional identifier of the filter condition by which values are filtered
     *
     * @param {string} [userQuery.attributeMarker] - Optional text identifier of the indexed attribute by which values are filtered
     *
     * @param {string} [userQuery.sortOrder] - Optional sorting order DESC | ASC
     *
     * @param {string} [userQuery.sortKey] - Optional field to sort by
     *
     * @returns  Array with ProductEntity objects
     */
    async getProductsEmptyPage(langCode = 'en_US', userQuery) {
        const query = { ...this._defaultQuery, ...userQuery };
        const result = await this._fetchRequests(`/empty-page?langCode=${this._LANGCODE_KEY}&` + this._queryParamsToString(query), langCode);
        return result;
    }
    /**
     * Search for all product page objects with pagination for the selected category.
     *
     * @param {number} [id] - Page identifier.
     *
     * @param {string | Array<string>} [langCode] - Language code parameter. Default "en_US"
     *
     * @param {IProductsQuery} [userQuery] - Optional set query parameters.
     * @param {number} [userQuery.limit] - Optional parameter for pagination, default is 0
     *
     * @param {number} [userQuery.offset] - Optional parameter for pagination, default is 30
     *
     * @param {string} [userQuery.statusMarker] - Optional identifier of the product page status
     *
     * @param {string} [userQuery.conditionValue] - Optional value that is being searched
     *
     * @param {string} [userQuery.conditionMarker] - Optional identifier of the filter condition by which values are filtered
     *
     * @param {string} [userQuery.attributeMarker] - Optional text identifier of the indexed attribute by which values are filtered
     *
     * @param {string} [userQuery.sortOrder] - Optional sorting order DESC | ASC
     *
     * @param {string} [userQuery.sortKey] - Optional field to sort by
     *
     * @returns  Array with ProductEntity objects
     */
    async getProductsPageById(id, langCode = 'en_US', userQuery) {
        const query = { ...this._defaultQuery, ...userQuery };
        const result = await this._fetchRequests(`/page/${id}?langCode=${this._LANGCODE_KEY}&` + this._queryParamsToString(query), langCode);
        return result;
    }
    /**
     * Search for all product page objects with pagination for the selected category (by its URL).
     *
     * @param {string} [url] - URL of the category page.
     *
     * @param {string | Array<string>} [langCode] - Language code parameter. Default "en_US"
     *
     * @param {IProductsQuery} [userQuery] - Optional set query parameters.
     * @param {number} [userQuery.limit] - Optional parameter for pagination, default is 0
     *
     * @param {number} [userQuery.offset] - Optional parameter for pagination, default is 30
     *
     * @param {string} [userQuery.statusMarker] - Optional identifier of the product page status
     *
     * @param {string} [userQuery.conditionValue] - Optional value that is being searched
     *
     * @param {string} [userQuery.conditionMarker] - Optional identifier of the filter condition by which values are filtered
     *
     * @param {string} [userQuery.attributeMarker] - Optional text identifier of the indexed attribute by which values are filtered
     *
     * @param {string} [userQuery.sortOrder] - Optional sorting order DESC | ASC
     *
     * @param {string} [userQuery.sortKey] - Optional field to sort by
     *
     * @returns  Array with ProductEntity objects
     */
    async getProductsPageByUrl(url, langCode = 'en_US', userQuery) {
        const query = { ...this._defaultQuery, ...userQuery };
        const result = await this._fetchRequests(`/page/url/${url}?langCode=${this._LANGCODE_KEY}&` + this._queryParamsToString(query), langCode);
        return result;
    }
    /**
     * Find all related product page objects.
     *
     * @param {number} [id] - Product page identifier for which to find relationship.
     *
     * @param {string | Array<string>} [langCode] - Language code parameter. Default "en_US"
     *
     * @param {IProductsQuery} [userQuery] - Optional set query parameters.
     * @param {number} [userQuery.limit] - Optional parameter for pagination, default is 0
     *
     * @param {number} [userQuery.offset] - Optional parameter for pagination, default is 30
     *
     * @param {string} [userQuery.sortOrder] - Optional sorting order DESC | ASC
     *
     * @param {string} [userQuery.sortKey] - Optional field to sort by
     *
     * @returns  Array with ProductEntity objects
     */
    async getRelatedProductsById(id, langCode = 'en_US', userQuery) {
        const query = { ...this._defaultQuery, ...userQuery };
        const result = await this._fetchRequests(`/${id}/related?langCode=${this._LANGCODE_KEY}&` + this._queryParamsToString(query), langCode);
        return result;
    }
    /**
     * Retrieve one product object.
     *
     * @param {number} [id] - Product id.
     *
     * @param {string | Array<string>} [langCode] Language code parameter. Default "en_US". You can also pass several language parameters ['en_US', 'it_IT']
     *
     * @returns  ProductEntity object.
     */
    async getProductById(id, langCode = 'en_US') {
        const result = await this._fetchRequests(`/${id}?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Find all product page objects with pagination and multiple filtering.
     *
     * @param {array} [data] - Request body.
     *
     * @param {string | Array<string>} [langCode] Language code parameter. Default "en_US".
     *
     * @example
     * const objects = [
     *
     *   {
     *
     *     attributeMarker: "price",
     *
     *     conditionMarker: "mth",
     *
     *     conditionValue: 1,
     *
     *     pageId: 0,
     *
     *   },
     *
     *   {
     *
     *     attributeMarker: "price",
     *
     *     conditionMarker: "lth",
     *
     *     conditionValue: 3,
     *
     *     pageId: 0,
     *
     *   },
     *
     * ];
     *
     * @param {IProductsQuery} [userQuery] - Optional set query parameters.
     * @param {number} [userQuery.limit] - Optional parameter for pagination, default is 0
     *
     * @param {number} [userQuery.offset] - Optional parameter for pagination, default is 30
     *
     * @param {string} [userQuery.sortOrder] - Optional sorting order DESC | ASC
     *
     * @param {string} [userQuery.sortKey] - Optional field to sort by
     *
     * @returns  Array with ProductEntity objects
     */
    async filterProduct(data, langCode = 'en_US', userQuery) {
        const query = { ...this._defaultQuery, ...userQuery };
        if (typeof langCode === 'string') {
            const response = await this._fetchPost(`/conditions-filter?langCode=${langCode}&` + this._queryParamsToString(query), data);
            const products = response.items;
            const result = products.map(item => this._normalizeData(item));
            return result;
        }
        else {
            const productList = [];
            await Promise.all(langCode.map(async (lang) => {
                const response = await this._fetchPost(`/conditions-filter?langCode=${lang}&` + this._queryParamsToString(query), data);
                productList.push(response.items);
            }));
            const result = [];
            productList.flat().forEach(product => {
                if (!result.find(prod => prod.id === product.id)) {
                    result.push(product);
                }
                else {
                    const relatedProduct = result.find(prod => prod.id === product.id);
                    Object.assign(relatedProduct.attributeValues, product.attributeValues);
                    Object.assign(relatedProduct.localizeInfos, product.localizeInfos);
                }
            });
            return result;
        }
    }
    /**
     * Quick search for product page objects with limited output.
     *
     * @param {string} [name] - Text to search product page objects (search is based on the title field of the localizeInfos object with language consideration)
     *
     * @param {string} [langCode] - Language code
     *
     * @returns  Array with ProductEntity objects
     */
    async searchProduct(name, langCode = 'en_US') {
        const searchProducts = await this._fetchGet(`/quick/search?lang=${langCode}&name=${name}`);
        const productsList = [];
        await Promise.all(searchProducts.map(async (product) => {
            await this.getProductById(product.id).then((result) => {
                productsList.push(result);
            });
        }));
        return productsList;
    }
}
exports.default = ProductApi;
